import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useCartStore } from '../store/cartStore';
import { formatCurrency } from '../utils/helpers';
import { idTail, isDefaultVariant, bxgyStockRules, bxgyMtoAttributes, bxgyCartItem } from '../utils/bxgyY';

/**
 * BXGY "Add <Y> to cart" picker (Oct 2026).
 *
 * Lists the Y items of one Buy-X-Get-Y offer with live stock at the current location, so staff can
 * add the offer item straight from the cart / checkout instead of searching for it.
 * - Staff can pick at most the offer's Y quantity (the units still needed): one item → radio
 *   buttons; more → checkboxes, each with its own quantity, total capped at that limit.
 * - Stock rules match product search: at a store only in-stock items; at a warehouse also
 *   made-to-order items (0 stock, continue-selling), tagged as MTO exactly like product search.
 * - If Shopify's stock check fails, the picker says so and offers Retry instead of showing 0.
 *
 * - After the items are added, the offer's code is APPLIED AUTOMATICALLY if it isn't on the cart yet
 *   (normal apply-discount, so every eligibility / combinability rule still applies). If the apply
 *   is refused, the items stay in the cart and the reason is shown.
 *
 * - Offer items already in the cart that count towards the offer slot being filled now (configured Y qty
 *   minus the units still missing) show pre-selected ("✓ Already in cart"). They use up part of the
 *   selection limit, can't be unticked here, and are never added again — only new picks are added.
 *   The radio/checkbox choice and the limit itself always follow the configured Y quantity.
 *
 * Props: code (offer code), needQty (Y units still needed), onClose(added: boolean).
 */
function BxgyAddYModal({ code, needQty = 1, onClose }) {
  const { addToCart, applyDiscount, cart, recordBxgyAutoAdded } = useCartStore();
  const locationId = localStorage.getItem('selectedLocation');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loc, setLoc] = useState({ type: '', name: '' });
  const [picks, setPicks] = useState([]); // [{ product, variant, qty, base }] — base = units already in the cart
  const [adding, setAdding] = useState(false);
  const [doneMsg, setDoneMsg] = useState(''); // items added but the code couldn't be applied
  // Single-variant offers skip the picker: 'pending' until the items load, 'auto' while the one variant is
  // being added (only a small "Adding…" card shows), 'off' = normal picker (several variants, or the auto
  // add couldn't run — out of stock, add/apply error — so staff see why).
  const [autoMode, setAutoMode] = useState('pending');

  const load = useCallback(() => {
    setData(null); setError(''); setPicks([]); setAutoMode('pending');
    return api.get(`/cart/bxgy-options/${encodeURIComponent(code)}`, { params: { locationId } })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || 'Could not load the offer items'));
  }, [code, locationId]);

  useEffect(() => {
    load();
    api.get('/locations').then(r => {
      const cur = (r.data.locations || []).find(l => l.id?.toString() === locationId?.toString());
      setLoc({ type: (cur?.type || '').toLowerCase(), name: cur?.name || '' });
    }).catch(() => {});
  }, [load, locationId]);

  const locType = loc.type || data?.locationType || 'store';
  // Selectable count = the offer's configured Y quantity (radio when 1, checkboxes when more) —
  // not the units still missing, so a partly-filled offer still offers the full choice.
  const limit = Math.max(1, data?.yQty || needQty || 1);
  const multi = limit > 1;
  const picked = picks.reduce((s, p) => s + p.qty, 0);
  const toAdd = picks.reduce((s, p) => s + (p.qty - (p.base || 0)), 0);
  const preselected = picks.reduce((s, p) => s + (p.base || 0), 0);

  // Pre-select the offer items already in the cart that belong to the slot being filled now:
  // configured Y qty − units still missing (0 when nothing of this slot is in the cart yet, e.g. a
  // repeat round, or a Y qty of 1). Items used by an earlier, complete round are not re-counted.
  useEffect(() => {
    if (!data) return;
    const linesOf = (variant) => (cart?.items || []).filter(it => idTail(it.variantId) === idTail(variant.id));
    const inCart = (variant) => linesOf(variant).reduce((s, it) => s + (parseInt(it.quantity, 10) || 0), 0);
    const yQty = Math.max(1, data.yQty || 1);
    let slot = needQty > 0 && needQty < yQty ? yQty - needQty : 0;
    const pre = [];
    (data.products || []).forEach(product => product.variants.forEach(variant => {
      const take = Math.min(slot, inCart(variant));
      if (take > 0) {
        const line = linesOf(variant)[0] || {};
        pre.push({ product, variant, qty: take, base: take, lineLocId: line.locationId != null ? idTail(line.locationId) : null });
        slot -= take;
      }
    }));
    setPicks(pre);

    // Only ONE Y variant in the offer → no choice to make: add it for the missing units and apply the code.
    const all = (data.products || []).flatMap(product => product.variants.map(variant => ({ product, variant })));
    if (all.length !== 1 || autoMode !== 'pending') { setAutoMode('off'); return; }
    const { product, variant } = all[0];
    const pick = pre[0] || { product, variant, qty: 0, base: 0 };
    const src = sourceFor(variant, [pick]);
    const missing = Math.max(1, needQty || 1);
    const add = !src ? 0 : (mtoAt(variant, src) ? missing : Math.min(missing, stockAt(variant, src).stock));
    if (add <= 0) { setAutoMode('off'); return; } // out of stock → show the picker with the reason
    const list = [{ ...pick, qty: (pick.base || 0) + add }];
    setPicks(list);
    setAutoMode('auto');
    handleAdd(list, true);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDefault = isDefaultVariant;
  const pickOf = (v, list = picks) => list.find(p => p.variant.id === v.id);

  // Where a gift unit comes from. The server sends stock per location (selected location, the user's
  // warehouse(s), and wherever the offer's items already sit in the cart):
  //  • a pre-selected in-cart item → ITS cart line's location ("+" adds to that same line);
  //  • otherwise the selected location if it can sell it, else a warehouse that can (store out of
  //    stock → warehouse), same stock/MTO rules as product search.
  const { curId, locations, locById, stockAt, mtoAt, usableAt, defaultSource } = bxgyStockRules(data, { id: locationId, name: loc.name, type: locType });
  const sourceFor = (v, list = picks) => {
    const pick = pickOf(v, list);
    if (pick && pick.base > 0 && pick.lineLocId) return usableAt(v, locById(pick.lineLocId)) ? locById(pick.lineLocId) : null;
    return defaultSource(v);
  };
  const stockOf = (v) => stockAt(v, sourceFor(v)).stock;
  const sellable = (v) => !!sourceFor(v);
  const maxFor = (v) => (stockOf(v) > 0 ? stockOf(v) : limit);
  const statusOf = (v) => {
    const src = sourceFor(v);
    if (!src) {
      const unknown = locations.some(l => stockAt(v, l).known === false);
      return { text: unknown ? "Couldn't check stock" : (locations.length > 1 ? 'Out of stock at store and warehouse' : 'Out of stock here'), bad: true };
    }
    const where = String(src.id) === curId ? '' : ` · ${src.name || 'Warehouse'}`;
    if (stockAt(v, src).stock > 0) return { text: `${stockAt(v, src).stock} in stock${where}`, bad: false };
    return { text: `Made to order${where}`, bad: false };
  };

  const toggle = (product, variant) => {
    const existing = pickOf(variant);
    if (existing && existing.base > 0) return; // already in the cart — stays selected
    if (!sellable(variant)) return;
    if (!multi) { setPicks([{ product, variant, qty: 1, base: 0 }]); return; }
    if (existing) { setPicks(ps => ps.filter(p => p.variant.id !== variant.id)); return; }
    if (picked >= limit) return; // already at the offer's Y quantity
    setPicks(ps => [...ps, { product, variant, qty: 1, base: 0 }]);
  };
  const changeQty = (variant, delta) => setPicks(ps => ps.map(p => {
    if (p.variant.id !== variant.id) return p;
    const next = p.qty + delta;
    if (next < Math.max(1, p.base || 0) || next > maxFor(variant) || (delta > 0 && (picked >= limit || !sellable(variant)))) return p;
    return { ...p, qty: next };
  }));

  // MTO attributes — the same rules product search applies at a warehouse.
  const mtoAttributes = (product, variant, src) => bxgyMtoAttributes(product, variant, mtoAt(variant, src));

  // list = the picks to add (the single-variant auto add passes its own, before state updates);
  // auto = that auto add: the units it adds are recorded so removing the code takes them out again.
  const handleAdd = async (list = picks, auto = false) => {
    const adds = list.reduce((s, p) => s + (p.qty - (p.base || 0)), 0);
    if (adds <= 0 || (adding && !auto)) return;
    setAdding(true); setError('');
    const added = [];
    for (const { product, variant, qty: total, base } of list) {
      const qty = total - (base || 0); // only units not already in the cart
      if (qty <= 0) continue;
      const src = sourceFor(variant, list);
      if (!src) { setAdding(false); setAutoMode('off'); setError(`${product.title} is out of stock`); return; }
      const customAttributes = await mtoAttributes(product, variant, src);
      const result = await addToCart(bxgyCartItem(product, variant, src, qty, stockAt(variant, src).stock,
        String(src.id) === curId ? loc.name : '', customAttributes));
      if (!result.success) {
        setAdding(false);
        setAutoMode('off');
        setError(result.error || `Failed to add ${product.title}`);
        return;
      }
      added.push({ variantId: idTail(variant.id), locationId: src.id != null ? String(src.id) : '', qty,
        customSize: (customAttributes || []).find(a => a.key === '_custom_size')?.value || null });
    }
    if (auto) recordBxgyAutoAdded(code, added);
    // Auto-apply the offer's code once its Y items are in the cart.
    const alreadyApplied = (useCartStore.getState().cart?.discounts || []).some(d => d.code === code);
    if (!alreadyApplied) {
      const applied = await applyDiscount(code, locationId, { autoAddY: false });
      if (!applied.success) {
        setAdding(false);
        setPicks([]);
        setAutoMode('off');
        setDoneMsg(`Items added, but ${code} could not be applied: ${applied.error || 'not eligible'}`);
        return;
      }
    }
    setAdding(false);
    onClose(true);
  };

  const products = data?.products || [];
  const rowStyle = (on, disabled) => ({
    display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', marginTop: '4px',
    border: `1.5px solid ${on ? '#f59e0b' : '#e5e7eb'}`, background: on ? '#fffbeb' : disabled ? '#f9fafb' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
  });
  const stepBtn = { width: '24px', height: '24px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', lineHeight: 1 };

  // Single-variant offer (or still loading): no picker — just a small status card.
  if (autoMode !== 'off' && !error) {
    const only = (data?.products || [])[0];
    const name = only ? `${only.title}${isDefault(only.variants[0] || {}) ? '' : ' - ' + only.variants[0].title}` : '';
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 3100, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
        <div style={{ background: '#fff', borderRadius: '12px', padding: '12px 18px', boxShadow: '0 12px 30px rgba(0,0,0,0.18)', borderTop: '3px solid #f59e0b', fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
          🎁 {autoMode === 'auto' ? `Adding ${name} — ${code}…` : `Loading ${code}…`}
        </div>
      </div>
    );
  }

  return (
    <div onClick={() => onClose(false)} style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', width: '460px', maxWidth: '92vw', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', borderTop: '4px solid #f59e0b' }}>
        <div style={{ padding: '16px 18px 10px' }}>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#92400e' }}>🎁 Add offer item — {code}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>
            {preselected > 0
              ? `${preselected} offer item${preselected > 1 ? 's' : ''} already in the cart — choose up to ${limit - preselected} more.`
              : multi ? `Choose up to ${limit} items for the offer.` : 'Choose 1 item for the offer.'}
          </div>
        </div>

        <div style={{ padding: '0 18px', overflowY: 'auto', flex: 1 }}>
          {doneMsg && <div style={{ background: '#fff7ed', color: '#9a3412', padding: '8px 10px', borderRadius: '8px', fontSize: '12px', marginBottom: '8px' }}>{doneMsg}</div>}
          {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 10px', borderRadius: '8px', fontSize: '12px', marginBottom: '8px' }}>{error}</div>}
          {data?.stockError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff7ed', color: '#9a3412', padding: '8px 10px', borderRadius: '8px', fontSize: '12px', marginBottom: '8px' }}>
              <span style={{ flex: 1 }}>Couldn't check stock for some items.</span>
              <button onClick={load} style={{ padding: '3px 10px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '11.5px', fontWeight: 700, cursor: 'pointer' }}>Retry</button>
            </div>
          )}
          {!data && !error && <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>Loading offer items…</div>}
          {data && products.length === 0 && <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>No offer items are set up for this offer.</div>}
          {products.map(p => {
            const single = p.variants.length === 1 && isDefault(p.variants[0]);
            return (
              <div key={p.productId} style={{ marginBottom: '12px' }}>
                {!single && <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151' }}>{p.title}</div>}
                {p.variants.map(v => {
                  const pick = pickOf(v);
                  const st = statusOf(v);
                  const full = multi && !pick && picked >= limit;
                  const inCart = pick && pick.base > 0;
                  const disabled = (!sellable(v) && !inCart) || full;
                  return (
                    <div key={v.id} onClick={() => !disabled && toggle(p, v)} style={rowStyle(!!pick, disabled)}>
                      <input type={multi ? 'checkbox' : 'radio'} readOnly checked={!!pick} disabled={disabled} style={{ accentColor: '#f59e0b', pointerEvents: 'none' }} />
                      {single && (v.imageSrc || p.image) && <img src={v.imageSrc || p.image} alt="" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '6px' }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#1f2937' }}>{single ? p.title : v.title}</div>
                        <div style={{ fontSize: '10.5px', color: st.bad ? '#dc2626' : '#6b7280' }}>{st.text}{v.sku ? ` · ${v.sku}` : ''}</div>
                        {inCart && <div style={{ display: 'inline-block', marginTop: '3px', fontSize: '10px', fontWeight: 700, color: '#15803d', background: '#dcfce7', padding: '1px 6px', borderRadius: '4px' }}>✓ Already in cart ({pick.base})</div>}
                      </div>
                      {multi && pick && (
                        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button style={stepBtn} onClick={() => changeQty(v, -1)} disabled={pick.qty <= Math.max(1, pick.base || 0)}>−</button>
                          <span style={{ minWidth: '14px', textAlign: 'center', fontSize: '12px', fontWeight: 700 }}>{pick.qty}</span>
                          <button style={stepBtn} onClick={() => changeQty(v, 1)} disabled={picked >= limit || pick.qty >= maxFor(v)}>+</button>
                        </div>
                      )}
                      <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#374151', flexShrink: 0 }}>{formatCurrency(v.price)}</div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Selected {picked} of {limit}</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => onClose(false)} style={{ padding: '7px 12px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          {doneMsg ? (
            <button onClick={() => onClose(true)} style={{ padding: '7px 14px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>Close</button>
          ) : (
          <button onClick={() => handleAdd()} disabled={toAdd <= 0 || adding} style={{ padding: '7px 14px', background: (toAdd <= 0 || adding) ? '#d1d5db' : '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12.5px', fontWeight: 700, cursor: (toAdd <= 0 || adding) ? 'not-allowed' : 'pointer' }}>
            {adding ? 'Adding…' : preselected > 0 && toAdd > 0 ? `Add ${toAdd} to cart` : 'Add to cart'}
          </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BxgyAddYModal;
